import React from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Truck, Hash } from 'lucide-react';

export function ShipOrderDialog({
  open,
  onOpenChange,
  order,
  shipForm,
  setShipForm,
  orderSerials,
  productTrackInfo,
  availableSerials,
  shipSerialAllocations,
  setShipSerialAllocations,
  shipLoading,
  onConfirmShip,
}) {
  if (!order) return null;

  const trackedItems = (order.items || []).filter(item => productTrackInfo[item.product_id]);
  const hasTracked = trackedItems.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Truck className="h-5 w-5 text-purple-400" />
            Ship Order #{order.id.slice(0, 8).toUpperCase()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-zinc-400 text-sm">Enter shipping details. The customer will be emailed with tracking info.</p>

          {/* Shipping Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Tracking Number *</Label>
              <Input
                value={shipForm.tracking_number}
                onChange={(e) => setShipForm({ ...shipForm, tracking_number: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="e.g., AP123456789AU"
                data-testid="ship-tracking-input"
              />
            </div>
            <div>
              <Label>Carrier</Label>
              <Select value={shipForm.carrier} onValueChange={(v) => setShipForm({ ...shipForm, carrier: v })}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="ship-carrier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Australia Post">Australia Post</SelectItem>
                  <SelectItem value="StarTrack">StarTrack</SelectItem>
                  <SelectItem value="TNT">TNT</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="Couriers Please">Couriers Please</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estimated Delivery</Label>
              <Input
                value={shipForm.estimated_delivery}
                onChange={(e) => setShipForm({ ...shipForm, estimated_delivery: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="e.g., 3-5 business days"
                data-testid="ship-delivery-input"
              />
            </div>
          </div>

          {/* Serial Number Allocation Section */}
          {hasTracked && (
            <div className="border-t border-zinc-800 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Hash className="h-4 w-4 text-[#78BE20]" />
                <h3 className="text-sm font-medium text-white">Serial Number Allocation</h3>
              </div>
              <p className="text-zinc-500 text-xs mb-3">
                The following items require serial numbers. Select from available stock or enter manually.
              </p>
              <div className="space-y-3">
                {trackedItems.map((item, idx) => {
                  const allocated = orderSerials.filter(s => s.product_id === item.product_id).length;
                  const remaining = item.quantity - allocated;
                  const avail = availableSerials[item.product_id] || [];

                  if (remaining <= 0) return (
                    <div key={idx} className="flex items-center gap-2 text-xs text-[#78BE20] p-2 bg-[#78BE20]/10 rounded" data-testid={`ship-serial-done-${idx}`}>
                      <Hash className="h-3 w-3" />
                      {item.product_name}: All {item.quantity} serial(s) allocated
                    </div>
                  );

                  return (
                    <div key={idx} className="bg-zinc-800 p-3 rounded" data-testid={`ship-serial-item-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-white text-sm font-medium">{item.product_name}</p>
                        <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                          {remaining} serial(s) needed
                        </Badge>
                      </div>
                      {avail.length > 0 && (
                        <div className="mb-2">
                          <p className="text-zinc-500 text-xs mb-1">Click to select from available stock:</p>
                          <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                            {avail.map((s, si) => {
                              const currentLines = (shipSerialAllocations[item.product_id] || '').split('\n').filter(Boolean);
                              const isSelected = currentLines.includes(s.serial_number);
                              return (
                                <button
                                  key={si}
                                  className={`text-xs px-2 py-0.5 rounded font-mono transition-colors ${
                                    isSelected
                                      ? 'bg-[#78BE20]/20 text-[#78BE20] border border-[#78BE20]/40'
                                      : 'bg-zinc-900 hover:bg-zinc-700 text-zinc-300'
                                  }`}
                                  onClick={() => {
                                    if (isSelected) {
                                      setShipSerialAllocations({ ...shipSerialAllocations, [item.product_id]: currentLines.filter(l => l !== s.serial_number).join('\n') });
                                    } else {
                                      setShipSerialAllocations({ ...shipSerialAllocations, [item.product_id]: [...currentLines, s.serial_number].join('\n') });
                                    }
                                  }}
                                  data-testid={`ship-pick-serial-${idx}-${si}`}
                                >
                                  {s.serial_number}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <Textarea
                        value={shipSerialAllocations[item.product_id] || ''}
                        onChange={(e) => setShipSerialAllocations({ ...shipSerialAllocations, [item.product_id]: e.target.value })}
                        placeholder={avail.length > 0 ? 'Selected serials appear here (or type manually)' : 'Enter serial numbers (one per line)'}
                        className="bg-zinc-900 border-zinc-700 font-mono text-xs"
                        rows={Math.min(remaining, 3)}
                        data-testid={`ship-serial-input-${idx}`}
                      />
                      <p className="text-zinc-600 text-xs mt-1">
                        {(shipSerialAllocations[item.product_id] || '').split('\n').filter(Boolean).length} of {remaining} entered
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              disabled={shipLoading}
              onClick={onConfirmShip}
              data-testid="confirm-ship-btn"
            >
              <Truck className="h-4 w-4 mr-2" />
              {shipLoading ? 'Processing...' : 'Mark as Shipped'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
